import { ReactNode, useMemo } from 'react';
import { useChartContext } from '../../ChartContext';
import { MeasureAssignment, YAxisAssignment } from '../assignmentComponents';
import { YAxisPosition } from '../../interfaces';
import FunnelAssignment from '../assignmentComponents/FunnelAssignment';
import {
  CentralChartSettingAccordionType,
  isCentralChartSettingVisible,
  checkIsAxisAssignmentVisible,
  zodCentralChartSettingAccordionType,
} from './utils';
interface IResult {
  title: string;
  children: ReactNode;
}

const useCentralChartSettingAccordionItems = (): IResult[] => {
  const {
    metadata: { queryType },
    settings: {
      chartSettings: { chartType, singleDayLabel },
    },
    displayedMeasures,
  } = useChartContext();

  const accordionItems: IResult[] = useMemo(() => {
    const items: IResult[] = [];

    const assignmentConfigs = isCentralChartSettingVisible({
      queryType,
      chartType,
      singleDayLabel,
    });

    if (!assignmentConfigs) {
      return [];
    }

    const assignmentMapper: Record<CentralChartSettingAccordionType, IResult> =
      {
        [CentralChartSettingAccordionType.MeasureAssignments]: {
          title: 'Measure Assignments',
          children: <MeasureAssignment />,
        },
        [CentralChartSettingAccordionType.FunnelAssignments]: {
          title: 'Funnel Assignments',
          children: <FunnelAssignment />,
        },
        [CentralChartSettingAccordionType.LeftYAxisAssignments]: {
          title: 'Left Y-Axis',
          children: (
            <YAxisAssignment
              yAxisPosition={YAxisPosition.LEFT}
              assignmentConfigs={assignmentConfigs}
            />
          ),
        },
        [CentralChartSettingAccordionType.RightYAxisAssignments]: {
          title: 'Right Y-Axis',
          children: (
            <YAxisAssignment
              yAxisPosition={YAxisPosition.RIGHT}
              assignmentConfigs={assignmentConfigs}
            />
          ),
        },
      };

    // iterate all assignments and push to accordion items if chart type supports it
    Object.keys(assignmentConfigs).forEach((strKey) => {
      const typedKey = zodCentralChartSettingAccordionType.parse(strKey);
      const isAllowAssignment = !!assignmentConfigs[typedKey];

      // hide the y-axis assignment if no measure is not visible on the axis
      const isAxisAssignmentVisible = checkIsAxisAssignmentVisible({
        queryType,
        chartType,
        singleDayLabel,
        displayedMeasures,
        accordionType: typedKey,
      });

      if (isAllowAssignment && isAxisAssignmentVisible) {
        items.push(assignmentMapper[typedKey]);
      }
    });

    return items;
  }, [queryType, chartType, singleDayLabel, displayedMeasures]);

  return accordionItems;
};

export default useCentralChartSettingAccordionItems;
